import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Phone, ArrowRight } from "lucide-react";
import { useCompany } from "@/context/CompanyContext";
import { trackEvent } from "@/lib/ga";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CtaBlock from "@/components/CtaBlock";
import GuaranteesGrid from "@/components/GuaranteesGrid";
import { GUARANTEES } from "@/data/guarantees";

export default function Guarantees() {
  const { company } = useCompany();
  const jsonLd = [{
    "@context": "https://schema.org", "@type": "ItemList",
    name: "Aquila Air & Heating Guarantees",
    itemListElement: GUARANTEES.map((g, i) => ({ "@type": "ListItem", position: i + 1, name: g.title, description: g.desc })),
  }];

  return (
    <>
      <Seo
        title="Our Guarantees | Aquila Air & Heating"
        description="Every job is backed by the Aquila Air & Heating guarantees — 100% Satisfaction, On-Time Installation, No-Lemon, Fair Price, Property Protection & more across the Space Coast."
        path="/guarantees/"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-navy text-white overflow-hidden dot-grid" data-testid="guarantees-hero">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-launch/10 to-transparent pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pb-20">
          <div className="[&_*]:!text-slate-300"><Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Our Guarantees" }]} /></div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-launch"><ShieldCheck className="h-4 w-4" /> Our Promise to You</p>
          <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">Guarantees That Back Every Job</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-200 leading-relaxed">
            We put our promises in writing. When you choose Aquila Air &amp; Heating, you are protected by real, written guarantees on our work, your comfort, and your investment.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link to="/contact-us" data-testid="guarantees-hero-cta" className="inline-flex items-center justify-center gap-2 bg-launch hover:bg-launch-dark text-white font-bold py-3.5 px-8 rounded-md shadow-lg transition-colors">
              Schedule Service <ArrowRight className="h-5 w-5" />
            </Link>
            <a href={`tel:${company.phone_href || ""}`} onClick={() => trackEvent("phone_click", { location: "guarantees_hero" })} data-testid="guarantees-hero-call" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold py-3.5 px-8 rounded-md transition-colors">
              <Phone className="h-5 w-5" /> Call {company.phone || ""}
            </a>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 md:py-24" data-testid="guarantees-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <GuaranteesGrid />
          <p className="mt-8 text-xs text-slate-500 max-w-3xl">Guarantees apply to qualifying work and are subject to applicable terms, conditions, and manufacturer requirements. Ask our team for full details for your specific system and service.</p>
        </div>
      </section>

      <CtaBlock heading="Comfort You Can Count On" />
    </>
  );
}
